import { BarChart3, ArrowUpRight } from "lucide-react";
import { Link } from "wouter";
import { PUBLIC_BUSINESS } from "../lib/public-business";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3" aria-label="DealerMind">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BarChart3 className="h-5 w-5" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold tracking-tight">DealerMind</span>
          </div>
          <Link
            href="/login"
            className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="link-log-in"
          >
            Log in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6">
        <section className="border-b py-16 sm:py-24">
          <p className="mb-5 text-sm font-semibold uppercase tracking-[0.16em] text-primary">
            Software for car dealerships
          </p>
          <h1 className="max-w-4xl text-3xl font-bold leading-tight tracking-tight sm:text-5xl sm:leading-[1.12]">
            DealerMind Intelligence LLC builds software for car dealerships:
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-muted-foreground">
            dealer analytics (floorplan cost analysis, inventory and operational performance), and
            DealerHub, an AI assistant that answers shopper questions, captures leads, and alerts
            dealership staff.
          </p>
        </section>

        <section className="grid gap-8 border-b py-12 sm:grid-cols-[minmax(0,1fr)_minmax(15rem,0.72fr)] sm:gap-14 sm:py-16">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              DealerMind
            </p>
            <h2 className="text-2xl font-semibold tracking-tight">Dealer analytics</h2>
            <p className="mt-4 max-w-2xl leading-7 text-muted-foreground">
              Floorplan cost analysis, inventory and operational performance.
            </p>
          </div>
          <div className="border-l-2 border-primary/30 pl-6 sm:pl-8">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              DealerHub
            </p>
            <h2 className="text-2xl font-semibold tracking-tight">Shopper assistant</h2>
            <p className="mt-4 leading-7 text-muted-foreground">
              An AI assistant that answers shopper questions, captures leads, and alerts
              dealership staff.
            </p>
          </div>
        </section>

        <section className="grid gap-6 border-b py-12 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-16">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              Messaging
            </p>
            <h2 className="text-2xl font-semibold tracking-tight">Text messaging</h2>
          </div>
          <div>
            <p className="leading-7 text-muted-foreground">
              DealerMind sends lead and account alerts by text to dealership staff who opt in inside
              DealerHub (Settings → Notifications). We never text car shoppers or send marketing
              through this program. Message frequency varies. Message and data rates may apply.
              Reply STOP to opt out, HELP for help.
            </p>
            <Link
              href="/sms-alerts"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="link-sms-program"
            >
              SMS program page
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section className="grid gap-6 py-12 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-16">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              Contact
            </p>
            <h2 className="text-2xl font-semibold tracking-tight">Get in touch</h2>
          </div>
          <address className="not-italic text-sm leading-7 text-muted-foreground">
            <p className="font-semibold text-foreground">{PUBLIC_BUSINESS.name}</p>
            <p>
              Email:{" "}
              <a className="underline underline-offset-4 hover:text-foreground" href={`mailto:${PUBLIC_BUSINESS.email}`} data-testid="link-business-email">
                {PUBLIC_BUSINESS.email}
              </a>
            </p>
            <p>Mailing Address: {PUBLIC_BUSINESS.mailingAddress}</p>
            <p>Phone: {PUBLIC_BUSINESS.phone}</p>
          </address>
        </section>
      </main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>&copy; 2026 DealerMind. All rights reserved.</span>
          <nav className="flex flex-wrap items-center gap-5" aria-label="Legal">
            <Link href="/sms-alerts" className="transition-colors hover:text-foreground" data-testid="footer-link-sms-alerts">
              SMS Alerts
            </Link>
            <Link href="/terms" className="transition-colors hover:text-foreground" data-testid="footer-link-terms">
              Terms of Service
            </Link>
            <Link href="/privacy" className="transition-colors hover:text-foreground" data-testid="footer-link-privacy">
              Privacy Policy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
